import { useMapsLibrary } from "@vis.gl/react-google-maps"
import { useCallback } from "react"

export type Address = {
  city: string
  countryCode: string
  formattedAddress: string
}

export type DetailedLocation = {
  lat: string
  lon: string
  country: string
  street: string
  locality: string
  postalCode: string
  countryCode: string
  formattedAddress: string
  companyName?: string
}

export const useReverseGeocoder = () => {
  const geocodingLib = useMapsLibrary("geocoding")

  return useCallback(
    async (lat: number | string, lng: number | string): Promise<Address> => {
      if (!geocodingLib) {
        throw new Error("Geocoding library not loaded")
      }

      const geocoder = new geocodingLib.Geocoder()

      const coordinates: google.maps.LatLngLiteral = {
        lat: typeof lat === "string" ? parseFloat(lat) : lat,
        lng: typeof lng === "string" ? parseFloat(lng) : lng,
      }

      return new Promise((resolve, reject) => {
        void geocoder.geocode({ location: coordinates }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            const result = results[0]
            let city = ""
            let country = ""
            result.address_components.forEach((component) => {
              if (component.types.includes("locality")) {
                city = component.long_name
              }
              if (component.types.includes("country")) {
                country = component.short_name
              }
            })
            resolve({
              city,
              countryCode: country,
              formattedAddress: result.formatted_address,
            })
          } else {
            reject(new Error(`Geocoding failed: ${status}`))
          }
        })
      })
    },
    [geocodingLib],
  )
}

export const useDetailedReverseGeocoder = () => {
  const geocodingLib = useMapsLibrary("geocoding")

  return useCallback(
    async (
      lat: number | string,
      lng: number | string,
      companyName?: string,
    ): Promise<DetailedLocation> => {
      if (!geocodingLib) {
        throw new Error("Geocoding library not loaded")
      }

      const geocoder = new geocodingLib.Geocoder()

      const coordinates: google.maps.LatLngLiteral = {
        lat: typeof lat === "string" ? parseFloat(lat) : lat,
        lng: typeof lng === "string" ? parseFloat(lng) : lng,
      }

      return new Promise((resolve, reject) => {
        void geocoder.geocode({ location: coordinates }, (results, status) => {
          if (status === "OK" && results && results[0]) {
            const result = results[0]
            let country = ""
            let street = ""
            let locality = ""
            let postalCode = ""
            let countryCode = ""

            result.address_components.forEach((component) => {
              if (component.types.includes("country")) {
                country = component.long_name
                countryCode = component.short_name
              }
              if (component.types.includes("locality")) {
                locality = component.long_name
              }
              if (component.types.includes("route")) {
                street = component.long_name
              }
              if (component.types.includes("street_number")) {
                street = `${component.long_name} ${street}`.trim()
              }
              if (component.types.includes("postal_code")) {
                postalCode = component.long_name
              }
            })

            resolve({
              lat: `${coordinates.lat}`,
              lon: `${coordinates.lng}`,
              country,
              street,
              locality,
              postalCode,
              countryCode,
              formattedAddress: result.formatted_address,
              companyName,
            })
          } else {
            reject(new Error(`Geocoding failed: ${status}`))
          }
        })
      })
    },
    [geocodingLib],
  )
}
